import ListingTable from '../ListingTable/ListingTable';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import OpportunityActions from './opportunityTableCell/OpportunityActions';
import OpportunityCompany from './opportunityTableCell/OpportunityCompany';
import { ApplicationRoundStatus, ApplicationStatus, UIOfflineDriveRoundDetail } from 'types';
import { useParams } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import MobileCardForOpportunityTable from './MobileCardForOpportunityTable';

/** Latest round per application (by created time) that can be undone — matches backend "max order" intent. */
function useUndoEligibleRoundIds(candidateRounds: UIOfflineDriveRoundDetail[] | undefined): Set<string> {
	return useMemo(() => {
		const rounds = candidateRounds || [];
		const bestByApp = new Map<string, UIOfflineDriveRoundDetail>();
		for (const r of rounds) {
			const appId = String(r.application._id);
			const cur = bestByApp.get(appId);
			if (!cur) {
				bestByApp.set(appId, r);
				continue;
			}
			const tNew = new Date(r.roundCreatedAt).getTime();
			const tOld = new Date(cur.roundCreatedAt).getTime();
			if (tNew > tOld || (tNew === tOld && String(r.roundId) > String(cur.roundId))) {
				bestByApp.set(appId, r);
			}
		}
		const undoableRound: ApplicationRoundStatus[] = [
			ApplicationRoundStatus.Started,
			ApplicationRoundStatus.NotStarted,
			ApplicationRoundStatus.Initiated,
		];
		const undoableApp: ApplicationStatus[] = [
			ApplicationStatus.ASSIGNMENT_IN_PROGRESS,
			ApplicationStatus.INTERVIEW_SCHEDULED,
		];
		const ids = new Set<string>();
		for (const r of bestByApp.values()) {
			if (undoableRound.includes(r.roundStatus) && undoableApp.includes(r.application?.status)) {
				ids.add(String(r.roundId));
			}
		}
		return ids;
	}, [candidateRounds]);
}

interface Props {
	candidateRounds: UIOfflineDriveRoundDetail[];
	isPlaced?: boolean;
	driveId: string;
}

export default function OpportunityTable({ driveId, candidateRounds, isPlaced }: Props) {
	const { positionId } = useParams();
	const isMobile = useMediaQuery('(max-width: 1280px)');
	const undoEligibleRoundIds = useUndoEligibleRoundIds(candidateRounds);
	const tableHeaders: { id: string; label: string; align?: 'center' | 'left' | 'right' | 'inherit' | 'justify' }[] = [
		{ id: 'company', label: 'Company' },
		{ id: 'application_status', label: 'Application Status', align: 'left' },
		{ id: 'round_status', label: 'Round Status' },
		{ id: 'salary_offering', label: 'Salary Offering' },
		{ id: 'bd_poc', label: 'BD POC' },
		{ id: 'round_type', label: 'Round Type' },
		{ id: 'created_at', label: 'Created At' },
		{ id: 'actions', label: 'Actions' },
	];

	const [tableRows, setTableRows] = useState<{ id: string; tableDataJsx: React.ReactNode }[][]>([]);
	useEffect(() => {
		const allTableRows: {
			id: string;
			tableDataJsx: ReactNode;
		}[][] =
			candidateRounds?.map((applicationRound, index) => [
				{
					id: 'company',
					tableDataJsx: (
						<OpportunityCompany
							companyName={applicationRound?.company?.name}
							role={applicationRound?.position?.title}
							logoUrl={applicationRound?.company?.logo}
							companyUrl={applicationRound?.company?.url || ''}
							offlineDriveRoom={applicationRound?.company?.offlineDriveRoom}
							positionLabel={applicationRound?.position?.positionLabel || ''}
						/>
					),
				},
				{
					id: 'application_status',
					tableDataJsx: <p>{applicationRound?.application?.status}</p>,
				},
				{
					id: 'round_status',
					tableDataJsx: <div className="flex items-center justify-center">{applicationRound?.roundStatus}</div>,
				},
				{
					id: 'salary_offering',
					tableDataJsx: (
						<div className="flex items-center justify-center">
							<p className="text-base font-[#28293D] font-medium">{`${applicationRound?.position?.salary?.min} - ${applicationRound?.position?.salary?.max} LPA`}</p>
						</div>
					),
				},
				{
					id: 'bd_poc',
					tableDataJsx: (
						<div className="flex items-center justify-center">
							<p className="text-base font-[#28293D] font-medium">{`${applicationRound?.company?.bdPoc?.name}`}</p>
						</div>
					),
				},
				{
					id: 'round_type',
					tableDataJsx: <div className="flex items-center justify-center">{applicationRound.customLabel}</div>,
				},
				{
					id: 'created_at',
					tableDataJsx: (
						<div className="flex items-center justify-center">
							{moment(applicationRound.roundCreatedAt).format('D MMM, hh:mma')}
						</div>
					),
				},
				{
					id: 'actions',
					tableDataJsx: (
						<div className="flex items-center justify-center">
							{applicationRound.roundStatus !== ApplicationRoundStatus.Rejected ? (
								<OpportunityActions
									driveId={driveId}
									applicationRound={applicationRound}
									isUserPlaced={isPlaced}
									showUndoAssign={undoEligibleRoundIds.has(String(applicationRound.roundId))}
								/>
							) : (
								<div className="flex items-center justify-center">
									<p className="text-base font-[#28293D] font-medium">{`-`}</p>
								</div>
							)}
						</div>
					),
				},
			]) || [];
		setTableRows(allTableRows);
	}, [candidateRounds, driveId, isPlaced, undoEligibleRoundIds]);

	return (
		<div className="flex max-w-[1440px] mx-auto">
			{!isMobile ? (
				<div className="w-full">
					<ListingTable tableHeaders={tableHeaders} tableRows={tableRows} showPagination={false} />
				</div>
			) : (
				<div className="flex flex-col gap-2 w-full">
					{candidateRounds
						?.filter(round => (positionId ? round?.position?._id?.toString() === positionId?.toString() : true))
						?.map((round: UIOfflineDriveRoundDetail) => {
							return (
								<div key={String(round.roundId)}>
									<MobileCardForOpportunityTable
										applicationRound={round}
										driveId={driveId}
										isUserPlaced={isPlaced}
										showUndoAssign={undoEligibleRoundIds.has(String(round.roundId))}
									/>
								</div>
							);
						})}
				</div>
			)}
		</div>
	);
}
